/* eslint-disable @typescript-eslint/no-explicit-any */
import { MapRefs } from '@uiw/react-baidu-map';
import {
  LABEL_FEE_CARD_POSITION,
  LABEL_CARD_POSITION,
  FEES_CARD_POSITION,
  DEFAULT_CARD_POSITION,
  PIN_HEIGHT,
  PIN_FLEXI_HEIGHT,
  hardCodedAllLocale,
} from '../../constants/lib/constants';
import {
  checkThresholdPercentage,
  getCurrentRateStrikeThroughBlock,
  getCurrentPriceRateBlock,
  getPriceByDecimalPoint,
  getCurrRateAmountBlock,
  getCurrentRatePointsBlock,
  getPriceCommaSeparator,
  getRateBlock,
} from '../../organisms/SearchResults/lib/helper';
import {
  DefaultValue,
  RateNodeTypeToBeUsed,
  PointsNodeTypeToBeUsed,
  STATUS_NOT_AVAILABLE,
  STATUS_MAX_OCCUPANCY_EXCEEDED,
} from '../../organisms/SearchResults/index.constant';
import { handleBMapRecenter } from '@marriott/mi-ui-library';
import { fetchCurrencyToLocale } from '../../utils';

/**
 * Function to generate label and class for secondary marker
 */
export const generateLabel = (
  dataValue: any,
  isMarker: boolean,
  selectedHotel: any,
  isFlexibleDateSearch: boolean,
  jsonContent: any,
  currentLocale?: string,
  noRatesAreAvailable?: boolean
) => {
  if (dataValue?.basicInformation?.newProperty) {
    return {
      label: `${
        !isMarker &&
        selectedHotel &&
        ((isFlexibleDateSearch ? selectedHotel[0]?.node?.id : selectedHotel[0]?.node.property.id) ||
          (isFlexibleDateSearch ? selectedHotel[1]?.node?.id : selectedHotel[1]?.node.property.id)) === dataValue.id
          ? `${jsonContent.newHotelLabel}`
          : !isMarker
          ? `${jsonContent.newHotelLabel}`
          : `${jsonContent.newTagLabel}`
      }`,
      className: `marker-label-add-on new-hotel new-hotel-${currentLocale} ${
        (isFlexibleDateSearch || noRatesAreAvailable) && 'flexible-new-hotel'
      }`,
    };
  } else if (dataValue?.basicInformation?.isRecentlyRenovated) {
    return {
      label: `${jsonContent.recentlyRenovatedLabel}`,
      className: 'marker-label-add-on recently-renovated',
    };
  } else if (dataValue?.basicInformation?.isFullyRenovated) {
    return {
      label: `${jsonContent.fullyRenovatedLabel}`,
      className: 'marker-label-add-on fully-renovated',
    };
  } else if (dataValue?.basicInformation?.hasRenovatedRooms) {
    return {
      label: `${jsonContent.renovatedRoomsLabel}`,
      className: 'marker-label-add-on renovated-room',
    };
  } else if (dataValue?.basicInformation?.newLobby) {
    return {
      label: `${jsonContent.renovatedLobbyLabel}`,
      className: 'marker-label-add-on renovated-lobby',
    };
  } else {
    return {
      label: '',
      className: '',
    };
  }
};

/**
 * Function to generate text of primary marker
 */
export const getLabelText = (
  edge: any,
  isFlexibleDateSearch: boolean,
  searchQueryState: any,
  isSpecialRateSearch: boolean,
  options: any,
  jsonContent: any,
  flexibleOrManyProperty: boolean,
  isErrorState: boolean,
  childCount: number,
  currentLocale: string,
  isShowFullPrice?: boolean,
  noRatesAreAvailable?: boolean
) => {
  let showStrikeThroughResult = false;
  let currEliteDiscountRate;
  let currRate = null;
  let currPointsRate;
  const currRatePointsBlock = getRateBlock(edge?.node?.rates, 'points');
  const rateNode = getCurrentPriceRateBlock(searchQueryState, edge.node.rates);

  // get rate points block for each property
  if (!isFlexibleDateSearch) {
    currPointsRate = getCurrentRatePointsBlock(edge?.node?.rates);
  }
  const pointsValue = flexibleOrManyProperty ? null : currPointsRate?.points ?? null;
  if (!isFlexibleDateSearch) {
    currRate = getCurrRateAmountBlock(rateNode);
  }
  // get total price for each property
  const totalPrice = flexibleOrManyProperty ? null : isShowFullPrice ? currRate?.totalAmount : null;
  // get rates for each property
  let priceInfo: any;
  // update logic for priceInfo depending on amountPlusMandatoryFees/amount
  if (currRate?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
    priceInfo = flexibleOrManyProperty ? null : currRate?.amountPlusMandatoryFees;
  } else {
    priceInfo = flexibleOrManyProperty ? null : currRate?.amount;
  }

  if (options?.rateRequestTypes?.length === 3) {
    currRate = isFlexibleDateSearch
      ? null
      : edge.node.rates[0]?.rateAmounts?.filter((key: any) => key.rateMode.code === PointsNodeTypeToBeUsed)[0];
  }
  if (options?.rateRequestTypes?.length === 2) {
    currEliteDiscountRate = isFlexibleDateSearch ? null : getCurrentRateStrikeThroughBlock(edge?.node?.rates);
  }

  const currRateNode = getCurrentPriceRateBlock(searchQueryState, edge?.node?.rates); // passing the complete API rate block to getCurrentPriceRateBlock to get the correct object(check API response it will make sense)
  const ratesData = getCurrRateAmountBlock(currRateNode); // extracting amount object from correct rate block
  const propertyData = isFlexibleDateSearch ? edge?.node : edge?.node?.property;
  const showUnavailableMessage = flexibleOrManyProperty
    ? false
    : (!Object.keys(ratesData?.amount || {}).length && propertyData?.basicInformation?.openingDate && !pointsValue) ||
      (rateNode?.status?.code === STATUS_NOT_AVAILABLE && currRatePointsBlock?.status?.code === STATUS_NOT_AVAILABLE);
  let hotelUnavailable = false;
  if (showUnavailableMessage) {
    hotelUnavailable = true;
  }
  const adultOnly = propertyData?.basicInformation?.isAdultsOnly;
  const isAdultOnlyEdgeCaseTriggered = adultOnly && childCount > 0;
  // check if the rates are present
  const isRatesPresent: boolean | undefined =
    Object.keys(currRate ?? {}).length > 0 || Object.keys(currPointsRate ?? {}).length > 0;
  if (
    (isFlexibleDateSearch ||
      (!isFlexibleDateSearch && Object.keys(ratesData?.amount || {}).length === 0 && !pointsValue)) &&
    !propertyData?.basicInformation?.newProperty &&
    new Date(
      isFlexibleDateSearch
        ? !edge?.node?.basicInformation?.openingDate
        : edge?.node?.property?.basicInformation?.openingDate
    ) < new Date()
  ) {
    hotelUnavailable = true;
  }

  let isHotelMaxOccupancyExceeded = false;

  if (
    !isFlexibleDateSearch &&
    (currRateNode?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED ||
      currRatePointsBlock?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED)
  ) {
    isHotelMaxOccupancyExceeded = true;
  }

  let isErrorStateValue: boolean = isErrorState;
  if (
    isErrorState &&
    (pointsValue ||
      totalPrice?.amount ||
      priceInfo?.amount ||
      isFlexibleDateSearch ||
      flexibleOrManyProperty ||
      isHotelMaxOccupancyExceeded ||
      hotelUnavailable)
  ) {
    isErrorStateValue = false;
  }

  let currVal: any;
  // update logic for currVal depending on amountPlusMandatoryFees/amount
  if (currRate?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
    currVal = flexibleOrManyProperty ? null : currRate?.amountPlusMandatoryFees;
  } else {
    currVal = flexibleOrManyProperty ? null : currRate?.amount;
  }
  let markerText = `${currVal?.amount} ${currVal?.currency}`;
  if (
    new Date(
      isFlexibleDateSearch
        ? !edge?.node?.basicInformation?.openingDate
        : edge?.node?.property?.basicInformation?.openingDate
    ) > new Date() &&
    (isFlexibleDateSearch || !isRatesPresent)
  ) {
    markerText = jsonContent.openingSoonLabel;
  } else if (!propertyData?.basicInformation?.bookable) {
    markerText = jsonContent?.rateUnavailableLabel;
  } else if (adultOnly && childCount > 0) {
    markerText = jsonContent?.unavailableLabel;
  } else if (
    (hotelUnavailable && !isFlexibleDateSearch && !noRatesAreAvailable) ||
    (isErrorStateValue && !isAdultOnlyEdgeCaseTriggered)
  ) {
    if (!isSpecialRateSearch) {
      // markerText = jsonContent?.soldOutLabel;
      markerText = jsonContent?.unavailableLabel;
    } else {
      markerText = jsonContent?.rateUnavailableLabel;
    }
  } else if (pointsValue) {
    markerText = `${getPriceCommaSeparator(pointsValue, searchQueryState, currentLocale)} ${jsonContent.pointsLabel}`;
  }
  // add logic for text to show on marker pin in case of points unavailability
  else if (!pointsValue && searchQueryState?.pointsEnable) {
    if (totalPrice?.amount) {
      markerText = `${getPriceByDecimalPoint(
        totalPrice?.amount,
        totalPrice?.decimalPoint,
        searchQueryState,
        currentLocale,
        totalPrice?.currency
      )} ${
        searchQueryState?.currentCurrency !== DefaultValue
          ? fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, searchQueryState?.currentCurrency)
          : fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, totalPrice?.currency)
      }`;
    }
    if (!totalPrice?.amount && priceInfo?.amount) {
      markerText = `${getPriceByDecimalPoint(
        priceInfo?.amount,
        priceInfo?.decimalPoint,
        searchQueryState,
        currentLocale,
        totalPrice?.currency
      )} ${
        searchQueryState?.currentCurrency !== DefaultValue
          ? fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, searchQueryState?.currentCurrency)
          : fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, priceInfo?.currency)
      }`;
    }
  } else if (currRate?.totalAmount?.amount && isShowFullPrice) {
    let priceInfo = currRate?.totalAmount;
    let EEOPriceInfo = currEliteDiscountRate?.totalAmount;

    if (searchQueryState.currentCurrency !== DefaultValue) {
      priceInfo = flexibleOrManyProperty ? null : currRate?.totalAmount;
      EEOPriceInfo = flexibleOrManyProperty ? null : currEliteDiscountRate?.totalAmount;
    }
    if (
      EEOPriceInfo &&
      EEOPriceInfo?.amount &&
      priceInfo &&
      priceInfo?.amount &&
      EEOPriceInfo?.amount < priceInfo?.amount
    ) {
      showStrikeThroughResult = checkThresholdPercentage(priceInfo?.amount, EEOPriceInfo?.amount);
    }
    markerText = `${
      showStrikeThroughResult
        ? getPriceByDecimalPoint(
            EEOPriceInfo?.amount,
            EEOPriceInfo?.decimalPoint as number,
            searchQueryState,
            currentLocale,
            EEOPriceInfo?.currency
          )
        : getPriceByDecimalPoint(
            priceInfo?.amount,
            priceInfo?.decimalPoint ?? 0,
            searchQueryState,
            currentLocale,
            priceInfo?.currency
          )
    } ${
      searchQueryState.currentCurrency === DefaultValue
        ? fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, priceInfo?.currency)
        : fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, searchQueryState.currentCurrency)
    }`;
  } else if (flexibleOrManyProperty || noRatesAreAvailable) {
    markerText = '.';
  } else {
    let priceInfoBlock: any;
    if (currRate?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
      priceInfoBlock = currRate?.amountPlusMandatoryFees;
    } else if (currRate?.amount) {
      priceInfoBlock = currRate?.amount;
    } else {
      priceInfoBlock = priceInfo;
    }
    let EEOPriceInfo;
    if (currEliteDiscountRate?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
      EEOPriceInfo = currEliteDiscountRate?.amountPlusMandatoryFees;
    } else {
      EEOPriceInfo = currEliteDiscountRate?.amount;
    }
    if (searchQueryState.currentCurrency !== DefaultValue) {
      if (currRate?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
        priceInfoBlock = flexibleOrManyProperty ? null : currRate?.amountPlusMandatoryFees;
      } else {
        priceInfoBlock = flexibleOrManyProperty ? null : currRate?.amount;
      }
      EEOPriceInfo = flexibleOrManyProperty ? null : currEliteDiscountRate?.amount;
    }
    if (
      EEOPriceInfo &&
      EEOPriceInfo?.amount &&
      priceInfoBlock &&
      priceInfoBlock?.amount &&
      EEOPriceInfo?.amount < priceInfoBlock?.amount
    ) {
      showStrikeThroughResult = checkThresholdPercentage(priceInfoBlock?.amount, EEOPriceInfo?.amount);
    }
    markerText = `${
      showStrikeThroughResult
        ? getPriceByDecimalPoint(
            EEOPriceInfo?.amount,
            EEOPriceInfo?.decimalPoint as number,
            searchQueryState,
            currentLocale,
            EEOPriceInfo?.currency
          )
        : getPriceByDecimalPoint(
            priceInfoBlock?.amount,
            priceInfoBlock?.decimalPoint as number,
            searchQueryState,
            currentLocale,
            priceInfoBlock?.currency
          )
    } ${
      searchQueryState.currentCurrency === DefaultValue
        ? fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, priceInfoBlock?.currency)
        : fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, searchQueryState.currentCurrency)
    }`;
  }
  return markerText;
};

/**
 * Function to recenter map
 */
export const handleMapRecenter = (markerLatLng: any, mapRef: any) => {
  mapRef?.panTo(markerLatLng);
};

/**
 * Function to handle scroll for mobile and tab view property cards
 */
export const handleScroll = (isFlexibleDateSearch: any, searchResults: any, activeIndex: any, mapRef: any) => {
  //adding activeIndex check to restrict recenter to propertycards only (not NEXT and BACK cards)
  if (searchResults && searchResults?.length && activeIndex < searchResults?.length) {
    handleMapRecenter(
      {
        lat: isFlexibleDateSearch
          ? searchResults[activeIndex]?.node?.basicInformation?.latitude
          : searchResults[activeIndex]?.node?.property?.basicInformation?.latitude,
        lng: isFlexibleDateSearch
          ? searchResults[activeIndex]?.node?.basicInformation?.longitude
          : searchResults[activeIndex]?.node?.property?.basicInformation?.longitude,
      },
      mapRef
    );
  }
};

/**
 * Function to compute logic for className of Primary marker and Secondary marker
 */
export const getPrimaryMapMarkerClass = (
  typeOfMarker: any,
  isViewportL?: any,
  labelText?: any,
  isFlexibleDateSearch?: any,
  selectedHotel?: any,
  edge?: any,
  uniqueMarkersState?: any,
  index?: any,
  hideVar?: any,
  flexibleOrManyProperty?: any,
  firstMarkersList?: any,
  jsonContent?: any,
  generatedLabel?: any,
  noRatesAreAvailable?: boolean
) => {
  let result = '';
  const unavailableText = jsonContent?.unavailableLabel;
  if (typeOfMarker === 'primary') {
    if (isViewportL && labelText !== unavailableText) {
      result = 'm-map-pin ';
    } else if (isViewportL && labelText === unavailableText) {
      result = 'm-map-pin ';
      if (noRatesAreAvailable) {
        result += 'marker-label-black ';
      } else {
        result += 'marker-label-grey ';
      }
    } else if (
      !isViewportL &&
      (isFlexibleDateSearch
        ? selectedHotel[0]?.node?.basicInformation?.latitude !== edge?.node?.basicInformation?.latitude &&
          selectedHotel[0]?.node?.basicInformation?.longitude !== edge?.node?.basicInformation?.longitude
        : selectedHotel[0]?.node?.property?.basicInformation?.latitude !==
            edge?.node?.property?.basicInformation?.latitude &&
          selectedHotel[0]?.node?.property?.basicInformation?.longitude !==
            edge?.node?.property?.basicInformation?.longitude) &&
      labelText !== unavailableText
    ) {
      result = 'm-map-pin ';
    } else if (
      !isViewportL &&
      (isFlexibleDateSearch
        ? selectedHotel[0]?.node?.basicInformation?.latitude !== edge?.node?.basicInformation?.latitude &&
          selectedHotel[0]?.node?.basicInformation?.longitude !== edge?.node?.basicInformation?.longitude
        : selectedHotel[0]?.node?.property?.basicInformation?.latitude !==
            edge?.node?.property?.basicInformation?.latitude &&
          selectedHotel[0]?.node?.property?.basicInformation?.longitude !==
            edge?.node?.property?.basicInformation?.longitude) &&
      labelText === unavailableText
    ) {
      result = 'm-map-pin ';
      if (noRatesAreAvailable) {
        result += 'marker-label-black ';
      } else {
        result += 'marker-label-grey ';
      }
    } else {
      result = 'm-map-pin t-label-alt-s active ';
      if (isFlexibleDateSearch || noRatesAreAvailable) {
        result += ' icon-status-member-elite flex-map-active-pin ';
      }
    }

    if (isFlexibleDateSearch || noRatesAreAvailable) {
      result += 'icon-status-member-elite flex-map-active-pin ';
    }

    if (hideVar) {
      result += 'hide-only ';
    }
    if (firstMarkersList && !uniqueMarkersState[index].duplicate) {
      result += 'first-marker ';
    }
    if (
      !isFlexibleDateSearch &&
      !noRatesAreAvailable &&
      (labelText === jsonContent?.openingSoonLabel || labelText === jsonContent?.rateUnavailableLabel) &&
      ((isFlexibleDateSearch || noRatesAreAvailable) && selectedHotel && selectedHotel?.length > 0
        ? selectedHotel[0]?.node?.id !== edge?.node?.id
        : selectedHotel[0]?.node?.property?.id !== edge?.node?.property?.id)
    ) {
      result += 'marker-label-grey ';
    }
    if (
      (isFlexibleDateSearch || noRatesAreAvailable) &&
      (labelText === jsonContent?.openingSoonLabel || labelText === jsonContent?.rateUnavailableLabel) &&
      ((isFlexibleDateSearch || noRatesAreAvailable) && selectedHotel && selectedHotel?.length > 0
        ? selectedHotel[0]?.node?.id !== edge?.node?.id
        : selectedHotel[0]?.node?.property?.id !== edge?.node?.property?.id)
    ) {
      result += 'marker-label-black ';
    }

    if (flexibleOrManyProperty || noRatesAreAvailable) {
      result += 'icon-status-member-elite flex-map-pin ';
    }
    if (
      isFlexibleDateSearch &&
      selectedHotel &&
      selectedHotel?.length > 0 &&
      new Date(edge?.node?.basicInformation?.openingDate) > new Date() &&
      selectedHotel[0]?.node?.id !== edge?.node?.id
    ) {
      result += 'marker-label-grey ';
    }
    result += `pin-${index} `;
  } else if (typeOfMarker === 'secondary') {
    result += `add-on-${index} ${generatedLabel?.className}`;
    if (hideVar) {
      result += 'hide-only ';
    }
    if (
      labelText === jsonContent.openingSoonLabel &&
      (isFlexibleDateSearch && selectedHotel && selectedHotel?.length > 0
        ? selectedHotel[0]?.node?.id !== edge?.node?.id
        : selectedHotel[0]?.node?.property?.id !== edge?.node?.property?.id)
    ) {
      result += 'opening-new-hotel';
    }
    if (
      labelText === jsonContent.openingSoonLabel &&
      (isFlexibleDateSearch && selectedHotel && selectedHotel?.length > 0
        ? selectedHotel[0]?.node?.id === edge?.node?.id
        : selectedHotel[0]?.node?.property?.id === edge?.node?.property?.id)
    ) {
      result += 'active-new-hotel';
    }

    // NEW: reduce top offset when an add-on label exists AND primary text is "Rate Unavailable"
    if (generatedLabel?.label && labelText === jsonContent?.rateUnavailableLabel) {
      result += ' addon-with-unavailable';
    }
  }
  return result;
};

/**
 * Function to compute logic for color of Primary marker
 */
export const getPrimaryMarkerColor = (
  isFlexibleDateSearch: any,
  selectedHotel: any,
  edge: any,
  noRatesAreAvailable: boolean
) => {
  if ((isFlexibleDateSearch || noRatesAreAvailable) && selectedHotel && selectedHotel?.length > 0) {
    return '';
  } else if (
    selectedHotel[0]?.node?.property?.basicInformation?.latitude !== edge?.node?.property?.basicInformation?.latitude ||
    selectedHotel[0]?.node?.property?.basicInformation?.longitude !== edge?.node?.property?.basicInformation?.longitude
  ) {
    return 'white';
  } else {
    return 'black';
  }
};

/**
 * heights helps to memoize the prev stack cards height
 */
const heights: any = {
  0: 0,
};

/**
 * To memoize the cardFeePresent for the previous stack cards
 */
const cardFeePresentMemo: boolean[] = [];
const cardBadgePresentMemo: string[] = [];

/**
 * Function to calculate the heights of prev stack cards height
 */
export const calculateCardHeight = (selectedHotel: any, index: number, hasLabel: any, cardFeePresent: boolean) => {
  // Base case: if index is 0, return 0
  if (index === 0 && selectedHotel?.length !== 0) {
    cardFeePresentMemo[index] = cardFeePresent; // Memoize the first card's cardFeePresent
    cardBadgePresentMemo[index] = hasLabel;
    return 0;
  }
  // Calculate the card height based on conditions
  let cardHeight = 0;
  const prevCardFeePresent = cardFeePresentMemo[index - 1];
  const prevCardBadgePresent = cardBadgePresentMemo[index - 1]?.length > 0;
  if (prevCardBadgePresent) {
    cardHeight = prevCardFeePresent ? LABEL_FEE_CARD_POSITION : LABEL_CARD_POSITION;
  } else {
    cardHeight = prevCardFeePresent ? FEES_CARD_POSITION : DEFAULT_CARD_POSITION;
  }
  // Accumulate the current card's height with the calculated height
  heights[index] = cardHeight;
  cardFeePresentMemo[index] = cardFeePresent; // Memoize the current card's cardFeePresent
  cardBadgePresentMemo[index] = hasLabel;
  // Recursively calculate the card height for the card above and pass the updated card height
  return heights[index - 1] + cardHeight;
};

/**
 * markerHeight helps to memoize the prev marker height in case of stack pins
 */
const markerHeight: any = { 0: 0 };
export const getMarkerHeight = (index: number, isFlexibleDateSearch: boolean) => {
  // Base case: If index is 0 or there's no selectedHotel data, return 20.
  if (index === 0) {
    return 20;
  }
  const PinHeight = isFlexibleDateSearch ? PIN_FLEXI_HEIGHT : PIN_HEIGHT;
  // Recursively get the height of the marker for the previous index.
  const previousMarkerHeight = getMarkerHeight(index - 1, isFlexibleDateSearch);
  // Set the current marker height in the markerHeight object and add the previous height.
  markerHeight[index] = PinHeight + previousMarkerHeight;
  return markerHeight[index];
};

/**
 * Function to find first marker in case of double stack markers
 */
export const findFirstMarker = (marker: any, firstMarkers: any): boolean => {
  let result = false;
  firstMarkers.forEach((fm: any) => {
    if (
      fm.latitude === marker.latitude &&
      fm.longitude === marker.longitude &&
      fm.duplicate !== marker.duplicate &&
      fm.propertyId !== marker.propertyId
    ) {
      result = true;
    }
  });
  return result;
};
/**
 * Function to find the index of the first duplicate marker in case of stack pins
 */
export const findFirstDuplicatePosition = (hotel: any) => {
  const markerEncountered: any = {}; // Use an object to store encountered elements and their positions
  for (let i = 0; i < hotel?.length; i++) {
    const currentElement: any = hotel[i]?.node?.property?.basicInformation?.latitude;
    // Check if the current element has been encountered before
    if (markerEncountered[currentElement] !== undefined) {
      return markerEncountered[currentElement]; // Return the position of the first duplicate
    } else {
      markerEncountered[currentElement] = i; // Store the position of the current element
    }
  }
  return -1; // Return -1 if no duplicates are found
};

export const getUniqueMarkers = (searchResults: any, isFlexibleDateSearch: any, uniqueMarkers: any) => {
  if (searchResults?.length) {
    const coords = new Set();
    const duplicates = new Map();
    // First pass to identify duplicates and store their minimum index
    for (const [i, obj] of searchResults.entries()) {
      const markerData = isFlexibleDateSearch ? obj?.node : obj?.node.property;
      const lat = markerData?.basicInformation?.latitude;
      const long = markerData?.basicInformation?.longitude;
      const coord = `${lat},${long}`;

      if (coords.has(coord)) {
        // If duplicate, store the minimum index
        const existingIndex = duplicates.get(coord);
        if (i < existingIndex) {
          duplicates.set(coord, i);
        }
      } else {
        coords.add(coord);
        duplicates.set(coord, i);
      }
    }

    // Second pass to build uniqueMarkers
    for (const [i, obj] of searchResults.entries()) {
      const markerData = isFlexibleDateSearch ? obj?.node : obj?.node.property;
      const lat = markerData?.basicInformation?.latitude;
      const long = markerData?.basicInformation?.longitude;
      const coord = `${lat},${long}`;

      const isDuplicate = duplicates.get(coord) !== i;

      uniqueMarkers.push({
        latitude: markerData?.basicInformation?.latitude,
        longitude: markerData?.basicInformation?.longitude,
        duplicate: isDuplicate,
        propertyId: markerData?.id,
        duplicateCount: 0,
      });
    }

    const duplicateCoordsParsed: any = []; // array to store the coordinates whenever we have parsed the coordinates more than once

    //below part of code is to parse through uniqueMarkers and find and assign a count to those coordinates which are duplicate.
    //so in case of single pin, duplicateCount will be 0 , for double stack pins , count will be 0 and 1 , for triple stack 0,1,2 and so on
    for (let n = 0; n < uniqueMarkers?.length; n++) {
      if (uniqueMarkers?.[n].duplicate) {
        const lat = uniqueMarkers?.[n]?.latitude;
        const long = uniqueMarkers?.[n]?.longitude;
        const coord = `${lat},${long}`;
        if (duplicateCoordsParsed?.includes(coord)) {
          uniqueMarkers[n].duplicateCount = uniqueMarkers?.[n]?.duplicateCount + 2;
        } else {
          uniqueMarkers[n].duplicateCount = uniqueMarkers?.[n]?.duplicateCount + 1;
          duplicateCoordsParsed.push(coord);
        }
      }
    }
    return uniqueMarkers;
  }
};

/**
 * Function to find and store duplicate coordinates
 */
export function findAndStoreDuplicates(data: any) {
  const duplicateCoordinates = new Set();
  const uniqueCoordinates = new Set();
  data?.forEach((item: any) => {
    const { node } = item;
    const { property } = node;
    const latitude = property?.basicInformation?.latitude ?? node?.basicInformation?.latitude;
    const coordinate = latitude;
    if (uniqueCoordinates.has(coordinate)) {
      duplicateCoordinates.add(coordinate);
    } else {
      uniqueCoordinates.add(coordinate);
    }
  });
  return Array.from(duplicateCoordinates);
}

/**
 * Function to set the centre of map when map is loaded and lat Lng values are not available
 */
export const handleMapCentre = (searchResults: any, query: any, setCentreValue: any, isFlexibleDateSearch: any) => {
  if (
    searchResults?.length &&
    (Number(query?.longitude) === 0 ||
      query?.longitude === null ||
      query?.longitude?.toString() === '' ||
      query?.latitude === undefined)
  ) {
    setCentreValue({
      lat: isFlexibleDateSearch
        ? Number(searchResults[0]?.node?.basicInformation?.latitude)
        : Number(searchResults[0]?.node?.property?.basicInformation?.latitude),
      lng: isFlexibleDateSearch
        ? Number(searchResults[0]?.node?.basicInformation?.longitude)
        : Number(searchResults[0]?.node?.property?.basicInformation?.longitude),
    });
  }
};

/**
 * Function to capture the marker click & save it in the context
 * @param {number}   index specifices the index of the card incase of stacked cards
 */
export const handleSelectedHotel = (
  index: number,
  searchResults: any,
  isViewportL: any,
  setActiveIndex: any,
  setSelectedHotel: any,
  isFlexibleDateSearch: any
) => {
  if (index < 0) {
    return;
  }
  const edge: any = searchResults && searchResults[index] ? searchResults[index] : null;
  if (!isViewportL) {
    setActiveIndex(index);
    setSelectedHotel(() => [edge]);
  } else {
    const finalData = isFlexibleDateSearch
      ? searchResults?.filter(
          (hotelList: any) =>
            hotelList?.node?.basicInformation?.latitude === edge?.node.basicInformation?.latitude &&
            hotelList?.node?.basicInformation?.longitude === edge?.node.basicInformation?.longitude &&
            hotelList?.node?.id !== edge?.node.id
        )
      : searchResults?.filter(
          (hotelList: any) =>
            hotelList.node.property.basicInformation?.latitude === edge?.node.property.basicInformation?.latitude &&
            hotelList.node.property.basicInformation?.longitude === edge?.node.property.basicInformation?.longitude &&
            hotelList.node.property.id !== edge?.node.property.id
        );

    if (finalData?.length) {
      setSelectedHotel([...finalData, edge]);
    } else {
      setSelectedHotel(() => [edge]);
    }
  }
};

//function to assign set boundaries - THIS WILL OMIT MAP CUSTOM ZOOM VALUES SETTING , hence removed setZoomValue from above function
export const fitBounds = (mapRef: any, bounds: any) => {
  if (mapRef && bounds) {
    mapRef.fitBounds(bounds);
  }
};

//function to set boundaries for maps based on the markers latitude and longitude
export const callFitBounds = (uniqueMarkersState: any, isLoaded: any, setBounds: any) => {
  if (uniqueMarkersState?.length > 0 && isLoaded) {
    const bounds = new window.google.maps.LatLngBounds();
    uniqueMarkersState?.forEach((marker: any) => {
      if (marker.latitude && marker.longitude) {
        bounds.extend({ lat: marker.latitude, lng: marker.longitude });
      }
    });
    setBounds(bounds);
  }
};

/**
 * Function to handle scroll for mobile and tab view property cards
 */
export const handleBMapScroll = (
  isFlexibleDateSearch: boolean,
  searchResults: Array<{
    node: {
      basicInformation: { latitude: number; longitude: number };
      property: { basicInformation: { latitude: number; longitude: number } };
    };
  }>,
  activeIndex: number,
  mapRef: MapRefs
) => {
  //adding activeIndex check to restrict recenter to propertycards only (not NEXT and BACK cards)
  if (searchResults && searchResults?.length && activeIndex < searchResults?.length) {
    handleBMapRecenter(
      isFlexibleDateSearch
        ? searchResults[activeIndex]?.node?.basicInformation?.latitude
        : searchResults[activeIndex]?.node?.property?.basicInformation?.latitude,
      isFlexibleDateSearch
        ? searchResults[activeIndex]?.node?.basicInformation?.longitude
        : searchResults[activeIndex]?.node?.property?.basicInformation?.longitude,
      mapRef
    );
  }
};

export const handleMarkerClick = (
  uniqueMarkersState: any,
  index: number,
  searchResults: any,
  isViewportL: any,
  setActiveIndex: any,
  setSelectedHotel: any,
  isFlexibleDateSearch: any,
  mapRef: any,
  setOpen: any
) => {
  handleMapRecenter(
    {
      lat: uniqueMarkersState[index].latitude,
      lng: uniqueMarkersState[index].longitude,
    },
    mapRef
  );
  setTimeout(() => {
    handleSelectedHotel(index, searchResults, isViewportL, setActiveIndex, setSelectedHotel, isFlexibleDateSearch);
    setOpen(true);
  }, 180);
  setTimeout(() => {
    // This QuerySelector can't be removed, as this class is from Google maps library.
    document.getElementsByClassName('gm-style-iw-tc')[1]?.classList.add('d-none');
  }, 200);
};
